import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import debug from 'debug';
import { saveBlob } from './saveBlob';
import { cellLines, type CellText } from './tableCellLines';

const log = debug('rois:utils:table-to-pdf');

if (import.meta.env.DEV) {
  debug.enable('rois:utils:table-to-pdf*');
}

interface PDFConfig {
  filename: string;
  title?: string;
  ignoreColumns: number[];
  filterByIDs: string[];
  filterPositionColumn: number;
  compositeKeyColumns?: number[];
  orientation?: 'portrait' | 'landscape';
}

const defaultConfig: PDFConfig = {
  filename: 'table.pdf',
  ignoreColumns: [],
  filterByIDs: [],
  filterPositionColumn: 1,
  orientation: 'landscape',
};

/**
 * Normalizes text extracted from DOM for reliable comparison.
 * Strips non-breaking spaces, zero-width characters, and normalizes whitespace.
 */
function normalizeText(text: string | null | undefined): string {
  if (text === null || text === undefined) return '';
  return text
    .replace(/\u00A0/g, ' ')       // non-breaking space -> regular space
    .replace(/\u200B/g, '')         // zero-width space
    .replace(/\u200C/g, '')         // zero-width non-joiner
    .replace(/\u200D/g, '')         // zero-width joiner
    .replace(/\uFEFF/g, '')         // BOM / zero-width no-break space
    .replace(/\s+/g, ' ')           // collapse multiple whitespace
    .trim();
}


/**
 * Celdas que cuentan para la exportación: descarta las marcadas con
 * `data-export-ignore` (p. ej. la columna de la bandera) ANTES de aplicar
 * cualquier índice, para que `ignoreColumns` y `filterPositionColumn` de cada
 * pantalla sigan apuntando donde siempre.
 */
function exportCells(row: HTMLTableRowElement | null | undefined): HTMLTableCellElement[] {
  return Array.from(row?.cells ?? []).filter(
    (c) => !c.hasAttribute('data-export-ignore')
  );
}

export class TableToPDF {
  private options: PDFConfig;
  private table: HTMLTableElement | null;
  private headers: string[] = [];
  private rows: CellText[][] = [];

  constructor(tableId: string, options: Partial<PDFConfig> = {}) {
    this.options = { ...defaultConfig, ...options };
    this.table = document.querySelector(tableId);
    this.init();
  }

  private init(): void {
    if (this.table && this.table.tHead) {
      this.headers = this.extractHeaders(this.table.tHead);
      this.rows = this.extractRows(this.table.tBodies);
    }
  }

  private extractHeaders(thead: HTMLTableSectionElement): string[] {
    const row = thead.rows.item(0);
    const cells = exportCells(row);
    const headers: string[] = [];
    for (let i = 0; i < cells.length; i++) {
      if (this.options.ignoreColumns.includes(i)) continue;
      headers.push(normalizeText(cells[i]?.textContent));
    }

    log('Extracted headers:', headers);
    return headers;
  }

  private extractRows(
    tbody: HTMLCollectionOf<HTMLTableSectionElement>
  ): CellText[][] {
    const rows: HTMLCollectionOf<HTMLTableRowElement> | undefined =
      tbody?.item(0)?.rows;
    const result: CellText[][] = [];

    log('extractRows - Debug Info:');
    log('Total rows found:', rows?.length || 0);
    log('Filter options:', this.options);

    // Pre-compute: check if we're using composite keys and normalize filter values once
    const isCompositeKey = this.options.filterByIDs.some((id) =>
      id.includes('|')
    );
    const normalizedFilterIDs = this.options.filterByIDs.map(normalizeText);
    const compositeKeyCols = this.options.compositeKeyColumns || [2, 3];

    log('isCompositeKey:', isCompositeKey);
    log('compositeKeyColumns:', compositeKeyCols);
    log('normalizedFilterIDs:', normalizedFilterIDs);

    if (rows) {
      for (let i = 0; i < rows.length; i++) {
        const tr = exportCells(rows?.item(i));

        let isIncluded = false;

        if (isCompositeKey) {
          // For composite keys, build the key from configured columns
          const keyParts = compositeKeyCols.map((colIdx) =>
            normalizeText(tr[colIdx]?.textContent)
          );
          const compositeKey = keyParts.join('|');

          isIncluded = normalizedFilterIDs.some(
            (filterKey) => filterKey === compositeKey
          );
          log(
            `Row ${i}: Composite key "${compositeKey}" (parts: ${JSON.stringify(keyParts)}), Included: ${isIncluded}`
          );
        } else {
          // Regular filtering by single column
          const cellContent = normalizeText(
            tr[this.options.filterPositionColumn]?.textContent
          );
          isIncluded = normalizedFilterIDs.some(
            (filterVal) => filterVal === cellContent
          );
          log(
            `Row ${i}: Cell[${this.options.filterPositionColumn}] = "${cellContent}", Included: ${isIncluded}`
          );
        }

        if (!isIncluded) {
          continue;
        }

        log(`Including row ${i} in PDF`);

        const pdfRow: CellText[] = [];
        for (let j = 0; j < tr.length; j++) {
          if (this.options.ignoreColumns.includes(j)) continue;
          pdfRow.push(cellLines(tr[j]?.textContent));
        }

        result.push(pdfRow);
      }
    }

    log('Final PDF rows:', result.length);
    return result;
  }

  getData(): { headers: string[]; rows: CellText[][] } {
    return { headers: this.headers, rows: this.rows };
  }

  async download(): Promise<void> {
    if (!this.table) return;

    const doc = new jsPDF({
      orientation: this.options.orientation || 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const startY = this.addHeader(doc);

    // Table
    autoTable(doc, {
      head: [this.headers],
      body: this.rows,
      startY,
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 10, right: 10, bottom: 15, left: 10 },
      didDrawPage: (data) => {
        // Page numbers at the bottom of each page
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `${data.pageNumber} / ${pageCount}`,
          doc.internal.pageSize.getWidth() - 20,
          doc.internal.pageSize.getHeight() - 8
        );
      },
    });

    // `doc.save()` de jsPDF descarga por su cuenta (blob + ancla) y eso muere
    // en el WebView, que no interpreta las URL `blob:`. Sacamos el Blob y lo
    // entregamos por el punto único.
    await saveBlob(doc.output('blob'), this.options.filename);
  }

  /**
   * Adds the title and generation date header to the PDF document.
   * Returns the Y position where the table should start.
   */
  private addHeader(doc: jsPDF): number {
    let currentY = 10;

    // Title
    if (this.options.title) {
      doc.setFontSize(16);
      doc.text(this.options.title, 14, 15);
      currentY = 22;
    }

    // Generation date
    doc.setFontSize(8);
    const dateStr = new Date().toLocaleDateString('ca-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    doc.text(dateStr, 14, currentY);

    return currentY + 4;
  }
}
